import {
  availableMetric,
  consumptionUnitFor,
  isMachine,
  pendingMetric,
  type MetricValue,
  type usageMetricFor,
} from '@fleetcore/shared'
import type { LiveVehicleState } from '@fleetcore/shared/telemetry'

import type { VehicleView } from './api-types'
import { measuredFuelLitres, reportedFuelPercent } from '../../lib/fuel-litres'

/**
 * The fork that makes an engin BTP a first-class asset rather than a car with a
 * different icon.
 *
 * `usageMetricFor()` and `consumptionUnitFor()` already encode it in shared, so the
 * job here is only to pick the right reading and to be honest when there is none.
 * A machine headlined "0 km aujourd'hui" is wrong even when the number is exactly
 * right — an excavator that worked nine hours on one slab travelled no distance at
 * all, and a manager reading that line concludes the machine was idle.
 */

export type UsageMetric = ReturnType<typeof usageMetricFor>
export type ConsumptionUnit = ReturnType<typeof consumptionUnitFor>

export interface UsageReading {
  metric: UsageMetric
  value: MetricValue<number>
  /** i18n key for the tile label — "Kilométrage" or "Heures moteur". */
  labelKey: string
  /** i18n key for the unit symbol shown after the number. */
  unitKey: string
  /** Decimals. An hour meter is billed in tenths; an odometer is not. */
  precision: number
}

/**
 * Why a reading is missing, as a key the catalog can turn into a next step.
 *
 * Two different absences: the platform has heard nothing from this vehicle at all
 * (check power, SIM, antenna), or it is talking but this particular value does not
 * come off the bus (check the CAN program number, or accept the ignition-derived
 * hour meter). One is a dead tracker, the other is a wiring decision.
 */
function missing(hasState: boolean, reasonKey: string): MetricValue<number> {
  return hasState
    ? pendingMetric('not_supported_by_vehicle', { reasonKey })
    : pendingMetric('pending_telemetry')
}

export function usageReading(vehicle: VehicleView, state: LiveVehicleState | null): UsageReading {
  const hasState = state !== null

  if (isMachine(vehicle)) {
    const hours = state?.engineHours ?? null
    return {
      metric: 'engine_hours',
      // The offset is the gap between the device's counter and the plate on the
      // machine. Adding it is what makes the figure match the one an operator reads
      // off the cab, which is the number a rental invoice is disputed against.
      value:
        hours === null
          ? missing(hasState, 'vehicles:empty.noHourMeter.title')
          : availableMetric(hours + vehicle.hourmeterOffsetH),
      labelKey: 'vehicles:detail.usage.engineHours',
      unitKey: 'common:units.hours',
      precision: 1,
    }
  }

  const km = state?.odometerKm ?? null
  return {
    metric: 'distance',
    value:
      km === null
        ? missing(hasState, 'vehicles:empty.noPosition.title')
        : availableMetric(km + vehicle.odometerOffsetKm),
    labelKey: 'vehicles:detail.usage.odometer',
    unitKey: 'common:units.km',
    precision: 0,
  }
}

export interface ConsumptionReading {
  unit: ConsumptionUnit
  value: MetricValue<number>
  labelKey: string
  /** i18n key for "L/100 km" or "L/h". */
  unitKey: string
}

/**
 * The configured consumption norm, in the unit the vehicle class is measured in.
 *
 * It is a reference figure entered by the fleet, not a measurement, so an absent
 * norm is `insufficient_history` rather than a telemetry problem — nothing about
 * the install will make it appear.
 */
export function consumptionNormReading(vehicle: VehicleView): ConsumptionReading {
  const unit = consumptionUnitFor(vehicle.vehicleClass)
  return {
    unit,
    value:
      vehicle.consumptionNorm === null
        ? pendingMetric('insufficient_history', { reasonKey: 'vehicles:detail.noConsumptionNorm' })
        : availableMetric(vehicle.consumptionNorm),
    labelKey: 'vehicles:detail.consumptionNorm',
    unitKey: unit === 'l_per_hour' ? 'common:units.lPerHour' : 'common:units.lPer100km',
  }
}

/**
 * Tank level: the percentage when the gauge reports one, else the litres (a car whose
 * bus gives litres and a fake 0 %). CAN-only, so its absence names the CAN bus.
 */
export function fuelLevelReading(state: LiveVehicleState | null): {
  readonly metric: MetricValue<number>
  readonly unit: 'percent' | 'litres'
} {
  const litres = measuredFuelLitres(state?.fuelLevelL ?? null, state?.fuelLevelPct ?? null)
  const pct = reportedFuelPercent(state?.fuelLevelPct ?? null, litres)
  if (pct !== null) return { metric: availableMetric(pct), unit: 'percent' }
  if (litres !== null) return { metric: availableMetric(litres), unit: 'litres' }
  return {
    metric: missing(state !== null, 'vehicles:empty.noCanData.title'),
    unit: 'percent',
  }
}

/** A numeric channel out of the live projection, or an explained absence. */
export function channelReading(
  state: LiveVehicleState | null,
  channel: string,
  reasonKey = 'vehicles:empty.noCanData.title',
): MetricValue<number> {
  const value = state?.channels?.[channel] ?? null
  if (value === null) return missing(state !== null, reasonKey)
  return availableMetric(value)
}
